import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Star, 
  Trophy, 
  Target,
  Brain,
  Zap,
  BookOpen,
  AlertCircle,
  Lightbulb,
  ArrowRight,
  ArrowLeft,
  RotateCcw
} from 'lucide-react';
import { Quiz, Question } from '../../types';

interface QuizPlayerProps {
  quiz: Quiz;
  onComplete: (score: number, timeSpent: number) => void;
  onExit: () => void;
}

interface UserAnswer {
  questionId: string;
  answer: number | string;
  timeSpent: number;
}

export const QuizPlayer: React.FC<QuizPlayerProps> = ({
  quiz,
  onComplete,
  onExit
}) => {
  // Safety check for quiz data
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-red-900 mb-2">Quiz Error</h3>
          <p className="text-red-700 mb-4">
            This quiz appears to be incomplete or corrupted. Please contact support.
          </p>
          <button
            onClick={onExit}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Exit Quiz
          </button>
        </div>
      </div>
    );
  }

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(quiz.timeLimit ? quiz.timeLimit * 60 : null);
  const [quizStartTime] = useState(Date.now());
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const currentQuestion = quiz?.questions?.[currentQuestionIndex];
  const progress = quiz?.questions?.length ? ((currentQuestionIndex + 1) / quiz.questions.length) * 100 : 0;

  useEffect(() => {
    if (timeRemaining === null) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const handleAnswerSelect = (answer: number | string) => {
    const questionStartTime = Date.now() - quizStartTime;
    
    setUserAnswers(prev => {
      const existingAnswer = prev.find(a => a.questionId === currentQuestion.id);
      if (existingAnswer) {
        return prev.map(a => 
          a.questionId === currentQuestion.id 
            ? { ...a, answer, timeSpent: questionStartTime }
            : a
        );
      } else {
        return [...prev, { questionId: currentQuestion.id, answer, timeSpent: questionStartTime }];
      }
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmitQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmitQuiz = () => {
    let totalScore = 0;
    let correctAnswers = 0;

    quiz.questions.forEach(question => {
      const userAnswer = userAnswers.find(a => a.questionId === question.id);
      if (userAnswer && userAnswer.answer === question.correctAnswer) {
        correctAnswers++;
        totalScore += question.points;
      }
    });

    const finalScore = Math.round((totalScore / quiz.questions.reduce((sum, q) => sum + q.points, 0)) * 100);
    const timeSpent = Math.round((Date.now() - quizStartTime) / 1000);

    setScore(finalScore);
    setShowResults(true);
    setIsCompleted(true);
    onComplete(finalScore, timeSpent);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'multiple_choice': return Target;
      case 'true_false': return CheckCircle;
      case 'fill_blank': return BookOpen;
      case 'matching': return Zap;
      case 'essay': return Brain;
      default: return Target;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (showResults) {
    const passed = score >= quiz.passScore;
    const userAnswer = userAnswers.find(a => a.questionId === currentQuestion.id);
    
    return (
      <div className="max-w-screen-md mx-auto w-full px-3 sm:px-4 py-6">
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
            passed ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {passed ? (
              <Trophy className="w-10 h-10 text-green-600" />
            ) : (
              <AlertCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {passed ? 'Congratulations!' : 'Keep Learning!'}
          </h2>
          
          <p className="text-lg text-gray-600 mb-6">
            You scored {score}% {passed ? 'and passed!' : `(need ${quiz.passScore}% to pass)`}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-semibold">Score</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{score}%</p>
            </div>
            
            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="font-semibold">Time</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {formatTime(Math.round((Date.now() - quizStartTime) / 1000))}
              </p>
            </div>
            
            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-5 h-5 text-green-500" />
                <span className="font-semibold">Questions</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {userAnswers.filter(a => {
                  const question = quiz.questions.find(q => q.id === a.questionId);
                  return question && a.answer === question.correctAnswer;
                }).length}/{quiz.questions.length}
              </p>
            </div>
          </div>
        </div>

        {/* Question Review */}
        <div className="space-y-4 sm:space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">Question Review</h3>
          {quiz.questions.map((question, index) => {
            const userAnswer = userAnswers.find(a => a.questionId === question.id);
            const isCorrect = userAnswer && userAnswer.answer === question.correctAnswer;
            const Icon = getQuestionIcon(question.type);
            
            return (
              <div key={question.id} className={`border rounded-xl p-4 ${
                isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    isCorrect ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-600">
                        Question {index + 1}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(question.difficulty)}`}>
                        {question.difficulty}
                      </span>
                    </div>
                    
                    <h4 className="font-medium text-gray-900 mb-3">{question.question}</h4>
                    
                    {question.type === 'multiple_choice' && (
                      <div className="space-y-2">
                        {question.options?.map((option, optIndex) => (
                          <div key={optIndex} className={`flex items-center space-x-2 p-2 rounded-lg ${
                            optIndex === question.correctAnswer 
                              ? 'bg-green-100 border border-green-300' 
                              : optIndex === userAnswer?.answer
                              ? 'bg-red-100 border border-red-300'
                              : 'bg-gray-50'
                          }`}>
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              optIndex === question.correctAnswer 
                                ? 'border-green-500 bg-green-100' 
                                : optIndex === userAnswer?.answer
                                ? 'border-red-500 bg-red-100'
                                : 'border-gray-300'
                            }`} />
                            <span className="text-sm">{option}</span>
                            {optIndex === question.correctAnswer && (
                              <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {question.explanation && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <Lightbulb className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">Explanation</span>
                        </div>
                        <p className="text-sm text-blue-700">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-8">
          <button
            onClick={onExit}
            className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
          >
            Exit Quiz
          </button>
          
          {!passed && (
            <button
              onClick={() => {
                setShowResults(false);
                setCurrentQuestionIndex(0);
                setUserAnswers([]);
                setIsCompleted(false);
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Retake Quiz</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  const userAnswer = userAnswers.find(a => a.questionId === currentQuestion?.id);
  const Icon = getQuestionIcon(currentQuestion?.type || 'multiple_choice');

  return (
    <div className="max-w-screen-md mx-auto w-full px-3 sm:px-4 py-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white border rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Quiz</h2>
              <p className="text-sm text-gray-600">
                Question {currentQuestionIndex + 1} of {quiz.questions.length}
              </p>
            </div>
          </div>
          
          {timeRemaining !== null && (
            <div className="flex items-center space-x-2 text-red-600">
              <Clock className="w-5 h-5" />
              <span className="font-mono text-lg">{formatTime(timeRemaining)}</span>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white border rounded-2xl p-4 sm:p-6">
        <div className="flex items-center space-x-2 mb-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(currentQuestion?.difficulty || 'medium')}`}>
            {currentQuestion?.difficulty || 'medium'}
          </span>
          <span className="text-sm text-gray-500">
            {currentQuestion?.points || 1} point{(currentQuestion?.points || 1) !== 1 ? 's' : ''}
          </span>
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          {currentQuestion?.question || 'Question not available'}
        </h3>

        {/* Answer Options */}
        {currentQuestion?.type === 'multiple_choice' && (
          <div className="space-y-3">
            {currentQuestion.options?.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  userAnswer?.answer === index
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    userAnswer?.answer === index
                      ? 'border-blue-500 bg-blue-100'
                      : 'border-gray-300'
                  }`}>
                    {userAnswer?.answer === index && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-700">{option}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {currentQuestion?.type === 'true_false' && (
          <div className="space-y-3">
            {['True', 'False'].map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  userAnswer?.answer === index
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    userAnswer?.answer === index
                      ? 'border-blue-500 bg-blue-100'
                      : 'border-gray-300'
                  }`}>
                    {userAnswer?.answer === index && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-700">{option}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={onExit}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
          >
            <XCircle className="w-4 h-4" />
            <span>Exit</span>
          </button>

          <button
            onClick={handleNext}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>{currentQuestionIndex === quiz.questions.length - 1 ? 'Submit' : 'Next'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
